import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import { createItem } from '@/modules/inventory/items/redux/ItemCRUD';
import {
  createDefaultItemForm,
  getItemGroupOptions,
  getItemManufacturerOptions,
  getItemSupplierOptions,
  getItemUomOptions,
  InventoryItemFormData,
  InventoryItemOption,
  loadItemGroupOptions,
  loadItemManufacturerOptions,
  loadItemSupplierOptions,
  loadItemUomOptions,
} from '@/modules/inventory/items/models/ItemModel';
import ItemForm from '@/modules/inventory/items/components/ItemForm';

import { RequestMaterialItemOption } from '../types';

interface RequestMaterialItemCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (item: RequestMaterialItemOption) => Promise<any>;
  loading?: boolean;
  companyId: string;
  companyName?: string;
}

export default function RequestMaterialItemCreateModal({
  open,
  onClose,
  onSave,
  companyId,
}: RequestMaterialItemCreateModalProps) {
  const [formData, setFormData] = useState<InventoryItemFormData>(() => createDefaultItemForm(companyId));
  const [groupOptions, setGroupOptions] = useState<InventoryItemOption[]>(() => getItemGroupOptions(companyId));
  const [supplierOptions, setSupplierOptions] = useState<InventoryItemOption[]>(() => getItemSupplierOptions(companyId));
  const [manufacturerOptions, setManufacturerOptions] = useState<InventoryItemOption[]>(() => getItemManufacturerOptions(companyId));
  const [uomOptions, setUomOptions] = useState<InventoryItemOption[]>(() => getItemUomOptions(companyId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const syncLocalOptions = (nextCompanyId: string) => {
    setGroupOptions(getItemGroupOptions(nextCompanyId));
    setSupplierOptions(getItemSupplierOptions(nextCompanyId));
    setManufacturerOptions(getItemManufacturerOptions(nextCompanyId));
    setUomOptions(getItemUomOptions(nextCompanyId));
  };

  useEffect(() => {
    if (!open || !companyId) return;
    setFormData(createDefaultItemForm(companyId));
    setError(null);
    setDirty(false);

    void Promise.all([
      loadItemGroupOptions(companyId),
      loadItemSupplierOptions(companyId),
      loadItemManufacturerOptions(companyId),
      loadItemUomOptions(companyId),
    ]).then(() => {
      syncLocalOptions(companyId);
    });
  }, [open, companyId]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Tên vật tư là bắt buộc.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await createItem(formData);
      const option: RequestMaterialItemOption = {
        id: created.id,
        name: created.name,
        code: created.code,
        supplier_id: created.supplier_id || undefined,
        supplier_name: created.supplier_name || undefined,
        unit_id: created.uom_id || undefined,
        unit_name: created.uom_name || undefined,
      };
      await onSave(option);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Có lỗi xảy ra khi tạo vật tư.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827' }}>
          Tạo vật tư mới
        </Typography>
        <IconButton onClick={onClose} size='small' disabled={saving}>
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Box sx={{ mb: 2, px: 1, py: 0.75, borderRadius: 1, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography sx={{ fontSize: '0.825rem', color: '#dc2626' }}>{error}</Typography>
          </Box>
        )}
        <ItemForm
          formData={formData}
          groupOptions={groupOptions}
          supplierOptions={supplierOptions}
          manufacturerOptions={manufacturerOptions}
          uomOptions={uomOptions}
          onChangeForm={(updates) => {
            const nextCompanyId = updates.company_id || formData.company_id;
            setFormData((prev) => ({ ...prev, ...updates }));
            setDirty(true);
            syncLocalOptions(nextCompanyId);
          }}
          onSave={() => { void handleSave(); }}
          onCancel={onClose}
          saving={saving}
          isDirty={dirty}
          hideHistoryTabs
        />
      </DialogContent>
    </Dialog>
  );
}
